import { Stack, styled } from '@mui/material';
import { useCallback } from 'react';
import { CustomViewFormConditionItem } from 'src/graphql/entities/custom-view';
import { getOperatorsByUiType } from 'src/graphql/entities/custom-view/custom-view-config/operators';
import { ConditionOperator } from 'src/graphql/types';
import { QueryOptionsValue } from 'src/pages/Settings/rules/use-rules-engine';
import { ChipItem } from './chip-item/chip-item';

export const ChipsContainer = styled(Stack)({
  flexDirection: 'row',
  gap: '0.6rem',
  flexWrap: 'wrap',
  alignItems: 'center',
  ml: '0.6rem',
});

export interface FilterChipsProps {
  /** list of filter conditions currently applied */
  conditions: CustomViewFormConditionItem[];
  /** receives the whole updated conditions array whenever one chip changes */
  onConditionsChange?: (updated: CustomViewFormConditionItem[]) => void;
  /** called when a chip’s “x” button is pressed */
  deleteCondition: (id: string) => void;
  /** map from `field.optionsQuery` to pre-fetched / live query options */
  queryOptions: Record<string, QueryOptionsValue | undefined>;
  /** extra props forwarded to the outer ChipsContainer (e.g. className, sx …) */
  chipsContainerProps?: Parameters<typeof ChipsContainer>[0];
  autoValueSelector?: boolean;
  /** shorter operator wording for narrow hosts, e.g. `In` as "is" instead of "is any of" */
  operatorDisplayOverrides?: Partial<Record<ConditionOperator, string>>;
}

export const FilterChips = ({
  conditions,
  onConditionsChange,
  deleteCondition,
  queryOptions,
  chipsContainerProps,
  autoValueSelector,
  operatorDisplayOverrides,
}: FilterChipsProps) => {
  const handleOneChange = useCallback(
    (idx: number) => (updated: CustomViewFormConditionItem) => {
      const next = [...conditions];
      next.splice(idx, 1, updated);
      onConditionsChange?.(next);
    },
    [conditions, onConditionsChange],
  );

  return (
    <ChipsContainer {...chipsContainerProps}>
      {conditions.map((c, idx) => (
        <ChipItem
          key={c.id}
          autoValueSelector={autoValueSelector}
          condition={c}
          onChange={handleOneChange(idx)}
          onDelete={deleteCondition}
          optionsSource={queryOptions[c.field?.optionsQuery ?? '']}
          operators={getOperatorsByUiType(c.field?.uiType)}
          operatorDisplayOverrides={operatorDisplayOverrides}
        />
      ))}
    </ChipsContainer>
  );
};
